import type { MapDef, RegionDef, TerritoryDef } from '@horde/engine';

/**
 * The Horde world map: 42 territories in 6 regions.
 *
 * Territories are groups of real countries (ISO 3166-1 alpha-3 codes, used by
 * the geometry build script). A few large countries are split by longitude;
 * see `split` in the build script.
 *
 * Adjacency is listed once per territory and validated to be symmetric.
 */

interface T {
  id: string;
  name: string;
  region: string;
  countries: readonly string[];
  neighbors: readonly string[];
}

const T: readonly T[] = [
  // North America -------------------------------------------------------------
  {
    id: 'greenland',
    name: 'Greenland',
    region: 'north-america',
    countries: ['GRL'],
    neighbors: ['canada', 'iceland'],
  },
  {
    id: 'canada',
    name: 'Canada',
    region: 'north-america',
    countries: ['CAN'],
    neighbors: ['greenland', 'united-states'],
  },
  {
    id: 'united-states',
    name: 'United States',
    region: 'north-america',
    countries: ['USA'],
    neighbors: ['canada', 'mexico', 'caribbean', 'siberia'],
  },
  {
    id: 'mexico',
    name: 'Mexico',
    region: 'north-america',
    countries: ['MEX'],
    neighbors: ['united-states', 'central-america', 'caribbean'],
  },
  {
    id: 'central-america',
    name: 'Central America',
    region: 'north-america',
    countries: ['GTM', 'BLZ', 'HND', 'SLV', 'NIC', 'CRI', 'PAN'],
    neighbors: ['mexico', 'caribbean', 'northern-south-america'],
  },
  {
    id: 'caribbean',
    name: 'Caribbean',
    region: 'north-america',
    countries: ['CUB', 'HTI', 'DOM', 'JAM', 'PRI', 'BHS', 'TTO'],
    neighbors: ['united-states', 'mexico', 'central-america', 'northern-south-america'],
  },

  // South America -------------------------------------------------------------
  {
    id: 'northern-south-america',
    name: 'Northern South America',
    region: 'south-america',
    countries: ['COL', 'VEN', 'GUY', 'SUR'],
    neighbors: ['central-america', 'caribbean', 'brazil', 'andes'],
  },
  {
    id: 'brazil',
    name: 'Brazil',
    region: 'south-america',
    countries: ['BRA'],
    neighbors: ['northern-south-america', 'andes', 'southern-cone', 'west-africa'],
  },
  {
    id: 'andes',
    name: 'Andes',
    region: 'south-america',
    countries: ['PER', 'ECU', 'BOL'],
    neighbors: ['northern-south-america', 'brazil', 'southern-cone'],
  },
  {
    id: 'southern-cone',
    name: 'Southern Cone',
    region: 'south-america',
    countries: ['ARG', 'CHL', 'URY', 'PRY'],
    neighbors: ['andes', 'brazil'],
  },

  // Europe --------------------------------------------------------------------
  {
    id: 'iceland',
    name: 'Iceland',
    region: 'europe',
    countries: ['ISL'],
    neighbors: ['greenland', 'british-isles', 'scandinavia'],
  },
  {
    id: 'british-isles',
    name: 'British Isles',
    region: 'europe',
    countries: ['GBR', 'IRL'],
    neighbors: ['iceland', 'scandinavia', 'western-europe'],
  },
  {
    id: 'scandinavia',
    name: 'Scandinavia',
    region: 'europe',
    countries: ['NOR', 'SWE', 'FIN', 'DNK'],
    neighbors: ['iceland', 'british-isles', 'central-europe', 'eastern-europe', 'western-russia'],
  },
  {
    id: 'western-europe',
    name: 'Western Europe',
    region: 'europe',
    countries: ['FRA', 'BEL', 'NLD', 'LUX', 'CHE'],
    neighbors: ['british-isles', 'iberia', 'central-europe', 'italy-balkans'],
  },
  {
    id: 'iberia',
    name: 'Iberia',
    region: 'europe',
    countries: ['ESP', 'PRT'],
    neighbors: ['western-europe', 'maghreb'],
  },
  {
    id: 'central-europe',
    name: 'Central Europe',
    region: 'europe',
    countries: ['DEU', 'POL', 'CZE', 'AUT', 'SVK', 'HUN', 'SVN'],
    neighbors: ['scandinavia', 'western-europe', 'italy-balkans', 'eastern-europe'],
  },
  {
    id: 'italy-balkans',
    name: 'Italy & Balkans',
    region: 'europe',
    countries: ['ITA', 'HRV', 'BIH', 'SRB', 'MNE', 'ALB', 'MKD', 'GRC', 'BGR', 'ROU', 'KOS'],
    neighbors: ['western-europe', 'central-europe', 'eastern-europe', 'middle-east', 'maghreb'],
  },
  {
    id: 'eastern-europe',
    name: 'Eastern Europe',
    region: 'europe',
    countries: ['UKR', 'BLR', 'MDA', 'LTU', 'LVA', 'EST'],
    neighbors: ['scandinavia', 'central-europe', 'italy-balkans', 'western-russia'],
  },
  {
    id: 'western-russia',
    name: 'Western Russia',
    region: 'europe',
    countries: ['RUS:west'],
    neighbors: ['scandinavia', 'eastern-europe', 'caucasus-persia', 'central-asia', 'siberia'],
  },

  // Africa --------------------------------------------------------------------
  {
    id: 'maghreb',
    name: 'Maghreb',
    region: 'africa',
    countries: ['MAR', 'DZA', 'TUN', 'LBY', 'ESH'],
    neighbors: ['iberia', 'italy-balkans', 'egypt-sudan', 'west-africa', 'central-africa'],
  },
  {
    id: 'egypt-sudan',
    name: 'Egypt & Sudan',
    region: 'africa',
    countries: ['EGY', 'SDN', 'SSD'],
    neighbors: ['maghreb', 'middle-east', 'central-africa', 'horn-of-africa', 'east-africa'],
  },
  {
    id: 'west-africa',
    name: 'West Africa',
    region: 'africa',
    countries: [
      'MRT',
      'MLI',
      'SEN',
      'GMB',
      'GNB',
      'GIN',
      'SLE',
      'LBR',
      'CIV',
      'GHA',
      'TGO',
      'BEN',
      'BFA',
      'NER',
      'NGA',
    ],
    neighbors: ['maghreb', 'central-africa', 'brazil'],
  },
  {
    id: 'central-africa',
    name: 'Central Africa',
    region: 'africa',
    countries: ['TCD', 'CMR', 'CAF', 'GAB', 'GNQ', 'COG', 'COD'],
    neighbors: ['maghreb', 'egypt-sudan', 'west-africa', 'east-africa', 'southern-africa'],
  },
  {
    id: 'horn-of-africa',
    name: 'Horn of Africa',
    region: 'africa',
    countries: ['ETH', 'ERI', 'DJI', 'SOM', 'SOL'],
    neighbors: ['egypt-sudan', 'east-africa', 'middle-east'],
  },
  {
    id: 'east-africa',
    name: 'East Africa',
    region: 'africa',
    countries: ['KEN', 'UGA', 'RWA', 'BDI', 'TZA'],
    neighbors: ['egypt-sudan', 'central-africa', 'horn-of-africa', 'southern-africa'],
  },
  {
    id: 'southern-africa',
    name: 'Southern Africa',
    region: 'africa',
    countries: ['AGO', 'ZMB', 'MWI', 'MOZ', 'ZWE', 'BWA', 'NAM', 'ZAF', 'LSO', 'SWZ', 'MDG'],
    neighbors: ['central-africa', 'east-africa'],
  },

  // Asia ----------------------------------------------------------------------
  {
    id: 'middle-east',
    name: 'Middle East',
    region: 'asia',
    countries: [
      'TUR',
      'SYR',
      'LBN',
      'ISR',
      'PSE',
      'JOR',
      'IRQ',
      'SAU',
      'YEM',
      'OMN',
      'ARE',
      'QAT',
      'KWT',
      'BHR',
      'CYP',
      'CYN',
    ],
    neighbors: ['italy-balkans', 'egypt-sudan', 'horn-of-africa', 'caucasus-persia'],
  },
  {
    id: 'caucasus-persia',
    name: 'Caucasus & Persia',
    region: 'asia',
    countries: ['GEO', 'ARM', 'AZE', 'IRN'],
    neighbors: ['middle-east', 'western-russia', 'central-asia', 'afghanistan-pakistan'],
  },
  {
    id: 'central-asia',
    name: 'Central Asia',
    region: 'asia',
    countries: ['KAZ', 'UZB', 'TKM', 'KGZ', 'TJK'],
    neighbors: ['western-russia', 'caucasus-persia', 'afghanistan-pakistan', 'siberia', 'china'],
  },
  {
    id: 'afghanistan-pakistan',
    name: 'Afghanistan & Pakistan',
    region: 'asia',
    countries: ['AFG', 'PAK'],
    neighbors: ['caucasus-persia', 'central-asia', 'china', 'india'],
  },
  {
    id: 'india',
    name: 'India',
    region: 'asia',
    countries: ['IND', 'NPL', 'BTN', 'BGD', 'LKA'],
    neighbors: ['afghanistan-pakistan', 'china', 'southeast-asia'],
  },
  {
    id: 'siberia',
    name: 'Siberia',
    region: 'asia',
    countries: ['RUS:east'],
    neighbors: [
      'western-russia',
      'central-asia',
      'mongolia',
      'china',
      'korea-japan',
      'united-states',
    ],
  },
  {
    id: 'mongolia',
    name: 'Mongolia',
    region: 'asia',
    countries: ['MNG'],
    neighbors: ['siberia', 'china'],
  },
  {
    id: 'china',
    name: 'China',
    region: 'asia',
    countries: ['CHN', 'TWN'],
    neighbors: [
      'central-asia',
      'afghanistan-pakistan',
      'india',
      'siberia',
      'mongolia',
      'korea-japan',
      'southeast-asia',
      'philippines',
    ],
  },
  {
    id: 'korea-japan',
    name: 'Korea & Japan',
    region: 'asia',
    countries: ['PRK', 'KOR', 'JPN'],
    neighbors: ['siberia', 'china'],
  },
  {
    id: 'southeast-asia',
    name: 'Southeast Asia',
    region: 'asia',
    countries: ['MMR', 'THA', 'LAO', 'KHM', 'VNM', 'MYS'],
    neighbors: ['india', 'china', 'indonesia'],
  },

  // Oceania -------------------------------------------------------------------
  {
    id: 'philippines',
    name: 'Philippines',
    region: 'oceania',
    countries: ['PHL'],
    neighbors: ['china', 'indonesia'],
  },
  {
    id: 'indonesia',
    name: 'Indonesia',
    region: 'oceania',
    countries: ['IDN', 'TLS', 'BRN'],
    neighbors: ['southeast-asia', 'philippines', 'melanesia', 'western-australia'],
  },
  {
    id: 'melanesia',
    name: 'New Guinea',
    region: 'oceania',
    countries: ['PNG'],
    neighbors: ['indonesia', 'eastern-australia'],
  },
  {
    id: 'western-australia',
    name: 'Western Australia',
    region: 'oceania',
    countries: ['AUS:west'],
    neighbors: ['indonesia', 'eastern-australia'],
  },
  {
    id: 'eastern-australia',
    name: 'Eastern Australia',
    region: 'oceania',
    countries: ['AUS:east'],
    neighbors: ['western-australia', 'melanesia', 'new-zealand'],
  },
  {
    id: 'new-zealand',
    name: 'New Zealand',
    region: 'oceania',
    countries: ['NZL'],
    neighbors: ['eastern-australia'],
  },
];

const R: readonly { id: string; name: string; bonus: number }[] = [
  { id: 'north-america', name: 'North America', bonus: 3 },
  { id: 'south-america', name: 'South America', bonus: 2 },
  { id: 'europe', name: 'Europe', bonus: 5 },
  { id: 'africa', name: 'Africa', bonus: 4 },
  { id: 'asia', name: 'Asia', bonus: 7 },
  { id: 'oceania', name: 'Oceania', bonus: 3 },
];

export const territories: readonly TerritoryDef[] = T.map(({ id, name, region, neighbors }) => ({
  id,
  name,
  region,
  neighbors,
}));

export const regions: readonly RegionDef[] = R.map((r) => ({
  ...r,
  territories: T.filter((t) => t.region === r.id).map((t) => t.id),
}));

/** ISO codes per territory, consumed by the geometry build script. */
export const countryCodes: Readonly<Record<string, readonly string[]>> = Object.fromEntries(
  T.map((t) => [t.id, t.countries]),
);

export const worldMap: MapDef = {
  id: 'world',
  name: 'World',
  territories,
  regions,
};
